import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Briefcase,
  Users,
  CalendarDays,
  Mail,
  Camera,
  Megaphone,
  CheckCircle2,
  Clock,
  ArrowRight,
} from "lucide-react";

interface HomeCanvasProps {
  orgId: string | null;
  onSuggest: (prompt: string) => void;
}

/**
 * The default canvas face: today's pipeline, what needs you, quick chips
 * to morph into other hubs. No tabs — every card is a portal.
 */
export function HomeCanvas({ orgId, onSuggest }: HomeCanvasProps) {
  const navigate = useNavigate();

  const { data: stats } = useQuery({
    queryKey: ["bridgette-home-stats", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      if (!orgId) return null;
      const start = new Date();
      start.setHours(0, 0, 0, 0);

      const [todayJobs, openLeads, draftProposals, recentJobs] = await Promise.all([
        supabase
          .from("jobs")
          .select("id, owner_name, property_address, status, created_at")
          .eq("org_id", orgId)
          .gte("created_at", start.toISOString())
          .order("created_at", { ascending: false })
          .limit(5),
        supabase
          .from("leads")
          .select("id", { count: "exact", head: true })
          .eq("org_id", orgId)
          .in("status", ["new", "contacted", "qualified"]),
        supabase
          .from("proposal_documents")
          .select("id", { count: "exact", head: true })
          .eq("org_id", orgId)
          .eq("status", "draft"),
        supabase
          .from("jobs")
          .select("id, owner_name, property_address, status, updated_at")
          .eq("org_id", orgId)
          .order("updated_at", { ascending: false })
          .limit(6),
      ]);

      return {
        todayJobs: todayJobs.data ?? [],
        openLeadsCount: openLeads.count ?? 0,
        draftProposalsCount: draftProposals.count ?? 0,
        recentJobs: recentJobs.data ?? [],
      };
    },
  });

  const chips = [
    { label: "Jobs", icon: Briefcase, to: "/jobs" },
    { label: "Leads", icon: Users, to: "/sales-hub" },
    { label: "Calendar", icon: CalendarDays, to: "/calendar" },
    { label: "Email", icon: Mail, to: "/email" },
    { label: "Photos", icon: Camera, to: "/photos-ai" },
    { label: "Campaigns", icon: Megaphone, to: "/campaigns" },
  ];

  const suggestions = [
    "Create a job for a claim Thursday",
    "Show today's schedule",
    "Draft a proposal for my last job",
    "Who hasn't been contacted this week?",
  ];

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      {/* Greeting */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Good to see you. What are we tackling?
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Type a command above or pick a suggestion.
        </p>
      </div>

      {/* Suggestion pills */}
      <div className="mb-8 flex flex-wrap gap-2">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onSuggest(s)}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition hover:border-primary/50 hover:bg-primary/5 hover:text-foreground"
          >
            {s}
          </button>
        ))}
      </div>

      {/* Pipeline grid */}
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Clock className="h-4 w-4" /> Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">{stats?.todayJobs.length ?? 0}</div>
            <p className="text-xs text-muted-foreground">New jobs created today</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Users className="h-4 w-4" /> Open leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">{stats?.openLeadsCount ?? 0}</div>
            <p className="text-xs text-muted-foreground">Need a touch</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" /> Draft proposals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">{stats?.draftProposalsCount ?? 0}</div>
            <p className="text-xs text-muted-foreground">Waiting to go out</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent jobs */}
      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Recent activity</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/jobs")} className="gap-1">
            All jobs <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
        <div className="grid gap-2">
          {(stats?.recentJobs ?? []).length === 0 && (
            <div className="rounded-lg border border-dashed border-border bg-card/40 p-6 text-center text-sm text-muted-foreground">
              No recent jobs yet. Try: <span className="text-foreground">"Create a job"</span>
            </div>
          )}
          {(stats?.recentJobs ?? []).map((j: any) => (
            <button
              key={j.id}
              type="button"
              onClick={() => navigate(`/jobs/${j.id}`)}
              className="group flex items-center justify-between rounded-lg border border-border/60 bg-card px-4 py-3 text-left transition hover:border-primary/40 hover:bg-card/80"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-foreground">
                  {j.owner_name || "Untitled job"}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {j.property_address || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  {j.status || "new"}
                </Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Hub chips */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-foreground">Jump to</h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-6">
          {chips.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.label}
                type="button"
                onClick={() => navigate(c.to)}
                className="flex flex-col items-center gap-2 rounded-xl border border-border/60 bg-card px-3 py-4 text-xs font-medium text-muted-foreground transition hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
              >
                <Icon className="h-5 w-5" />
                {c.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
